import ArrowDownSvg from "@/src/assets/icons/icon-chevron-down.svg";
import { ReactComponent as DownloadSvg } from "@/src/assets/icons/icon-download-black.svg";
import DownloadIndigoSvg from "@/src/assets/icons/icon-download-indigo.svg";
import { ReactComponent as EditSvg } from "@/src/assets/icons/icon-edit-pencel.svg";
import { ReactComponent as ErrorCircleSvg } from "@/src/assets/icons/icon-error-circle-fill.svg";
import ResetSvg from "@/src/assets/icons/icon-filter-reset.svg";
import PreviewSvg from "@/src/assets/icons/icon-preview-indigo.svg";
import { Button } from "@/src/components/atom/Button";
import IconButton from "@/src/components/atom/Button/IconButton";
import Checkbox from "@/src/components/atom/Checkbox";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import Flex from "@/src/components/molecule/Flex";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import TableBlueText from "@/src/components/molecule/TableBlueText";
import ViewPhotoCollectionDialog, {
  ContainerMediaData,
} from "@/src/components/organism/ViewPhotoCollectionDialog";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import useAlert from "@/src/hooks/useAlert";
import { useCompanyLocalDate } from "@/src/hooks/useCompanyLocalDate";
import TaskThumbnail from "@/src/pages/exporter/Task/components/detail/TaskThumbnail";
import EditContainerInformationDialog from "@/src/pages/exporter/Task/components/detail/dialog/EditContainerInformationDialog";
import {
  renderContainerWeighCheckedStatus,
  renderIsWritedInAppStatus,
  renderLoadingContainerStatusBadge,
} from "@/src/pages/exporter/Task/utils/renderTaskStatusBadge";
import { useAppSelector } from "@/src/store";
import { useGetPhotoTypeConfigQuery } from "@/src/store/apis/photoType";
import { useContainerIdToSeaVantageUrlMutation } from "@/src/store/apis/seavantage";
import {
  taskDetailApi,
  useLazyGetImporterTaskDetailContainerQuery,
  useLazyGetTaskDetailContainerQuery,
  useTaskDetailPrefetch,
  useUpdateContainersWeightStatusMutation,
} from "@/src/store/apis/tasks/taskDetail";
import {
  ContainerDetailViewDto,
  ContainerMediaType,
  CustomPhotoTypeItem,
  CustomPhotoTypeMediaGroup,
  PhotoTypeMediaInfo,
  SimpleContainerMediaDto,
  TaskPhotoTypeRequestResponseDto,
} from "@/src/store/apis/tasks/taskDetail/interface";
import { slideDown, slideUp } from "@/src/styles/animation";
import colorSet, { ColorType } from "@/src/styles/color";
import { downloadFile } from "@/src/utils/downloadFile";
import { formatSingleContainerMedia } from "@/src/utils/format";
import getDateDifference from "@/src/utils/getDateDifference";
import { downloadImageWithExif } from "@/src/utils/imageMetadataUtils";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@radix-ui/react-accordion";
import dayjs from "dayjs";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { PulseLoader } from "react-spinners";
import styled, { css, CSSProperties } from "styled-components";
import {
  ContainerAccordionItemProps,
  containerCountMediaMap,
  countKeyConvertToContainerType,
  DEFAULT_DISPLAY_ORDER,
  EditContainerTabType,
  FALLBACK_PHOTO_TYPE_TITLES,
  ImageResourceItem,
  isPhotoTypeCode,
  SelectedLoadingData,
  TaskContainerDialogType,
} from "./types";

type CustomPhotoTypeMediaDto = CustomPhotoTypeMediaGroup["medias"][number];

const createMediaDataFromMedia = (media: CustomPhotoTypeMediaDto): ContainerMediaData => ({
  createAt: media.containerMediaCreatedAt,
  latitude: media.coordinate?.latitude ?? undefined,
  longitude: media.coordinate?.longitude ?? undefined,
  zoomLevel: media.coordinate?.zoomLevel ? Number(media.coordinate?.zoomLevel) : undefined,
  mediaCoordinateRange: media.coordinate?.mediaCoordinateRange || undefined,
  originalFileName: media.containerPhotoSimpleMedia.originalFileName ?? undefined,
  mediaUrl: media.containerPhotoSimpleMedia?.mediaUrl ?? undefined,
});

const createImageListWithPlaceholders = (
  uploadedImages: (string | undefined)[],
  targetCount: number
): (string | undefined)[] => [
  ...uploadedImages,
  ...Array.from({ length: targetCount }, () => undefined),
].slice(0, targetCount);

interface CreateImageResourceItemParams {
  medias: SimpleContainerMediaDto[] | null;
  requiredCount: number;
  title: string;
  subTitle?: string;
  remark?: string;
  displayOrder: number;
}

const createImageResourceItem = (params: CreateImageResourceItemParams): ImageResourceItem => {
  const { medias, requiredCount, title, subTitle, remark, displayOrder } = params;
  const mediaDataList = medias?.map(createMediaDataFromMedia) || [];
  const imageUrls = medias?.map((m) => m.containerPhotoSimpleMedia?.mediaUrl) || [];

  return {
    isUsed: true,
    imageList: createImageListWithPlaceholders(imageUrls, requiredCount),
    title,
    subTitle,
    remark,
    displayOrder,
    mediaDataList,
  };
};

function ContainerAccordionItem({
  role,
  containerId,
  onCheckChange,
  containerNo,
  sealNo,
  containerStatus,
  containerWeighingStatus,
  isUnableEdit = false,
  containerCheckList,
  index,
  extraRemark,
  photoTypeRequests,
}: ContainerAccordionItemProps) {
  const { t } = useTranslation();
  const alert = useAlert();
  const isFetched = useRef(false);
  const id = useId();
  const loadingImageRef = useRef<HTMLDivElement>(null);
  const loadingWeightRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const userSession = useAppSelector((state) => state.auth.user);
  const gmtOffset = userSession?.exporter?.gmtOffset || 0;

  const isExporter = role === "exporter";

  const [selectDialog, setSelectDialog] = useState<TaskContainerDialogType>(null);
  const [startIndex, setStartIndex] = useState<number>();
  const [selectLoading, setSelectLoading] = useState<SelectedLoadingData>(null);
  const [isRefetched, setIsRefetched] = useState<boolean>(false);
  const [selectEditTab, setSelectEditTab] = useState<EditContainerTabType | null>(null);
  const [isPreviewDownloadLoading, setIsPreviewDownloadLoading] = useState(false);
  const [selectImageUrl, setSelectImage] = useState<ContainerMediaData | null>(null);

  // API Hooks - role 기반 분기
  const { data: photoTypeConfig } = useGetPhotoTypeConfigQuery(undefined, {
    skip: !isExporter,
    refetchOnMountOrArgChange: true,
  });

  const [getExporterContainer, exporterContainerData] = useLazyGetTaskDetailContainerQuery();
  const [getImporterContainer, importerContainerData] = useLazyGetImporterTaskDetailContainerQuery();
  const [containerIdToSeaVantageUrl] = useContainerIdToSeaVantageUrlMutation();
  const [updateContainersWeightStatus] = useUpdateContainersWeightStatusMutation();

  const prefetchExporter = useTaskDetailPrefetch("getTaskDetailContainer");
  const prefetchImporter = useTaskDetailPrefetch("getImporterTaskDetailContainer");

  const { currentData: exporterCurrentData, isFetching: exporterFetching } =
    taskDetailApi.endpoints.getTaskDetailContainer.useQueryState({ id: containerId });
  const { currentData: importerCurrentData, isFetching: importerFetching } =
    taskDetailApi.endpoints.getImporterTaskDetailContainer.useQueryState({ id: containerId });

  const currentData = isExporter ? exporterCurrentData : importerCurrentData;
  const isFetching = isExporter ? exporterFetching : importerFetching;
  const loadingDetailContainerData = isExporter ? exporterContainerData : importerContainerData;

  const photoSubmittedDate = useCompanyLocalDate({
    date: currentData?.photoSubmittedDateAt || "",
    format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
  });

  const isContainerCheckCompleted = isRefetched
    ? loadingDetailContainerData.currentData?.containerWeighingStatus
    : containerWeighingStatus;

  const handlePrefetch = async () => {
    try {
      const prefetch = isExporter ? prefetchExporter : prefetchImporter;
      await prefetch({ id: containerId }, { force: !isFetched.current });
      isFetched.current = true;
    } catch (e: any) {
      const message = Array.isArray(e.data?.message) ? e.data.message[0] : e.data?.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleContainerRefetch = async () => {
    try {
      const getContainer = isExporter ? getExporterContainer : getImporterContainer;
      await getContainer({ id: containerId }).unwrap();
    } catch (e: any) {
      const message = Array.isArray(e.data?.message) ? e.data.message[0] : e.data?.message;
      alert.showAlert({ type: "error", message });
    } finally {
      setIsRefetched(true);
    }
  };

  const handleContainerNoClick = async () => {
    try {
      const { cargoTrackUrl } = await containerIdToSeaVantageUrl({ containerId }).unwrap();
      window.open(cargoTrackUrl, "_blank");
    } catch (e: any) {
      const message = Array.isArray(e.data?.message) ? e.data.message[0] : e.data?.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleConfirmClick = async () => {
    try {
      await updateContainersWeightStatus({
        id: Number(params.id),
        containerIdList: [containerId],
      });
      alert.showAlert({ type: "success", message: t("alert:containerConfirmSuccess") });
      handleContainerRefetch();
    } catch (e) {
      alert.showAlert({ type: "error", message: t("alert:containerConfirmError") });
    }
  };

  const getPhotoTypeSettings = useCallback(
    (containerType: ContainerMediaType | undefined) => {
      if (!containerType || !photoTypeConfig?.photoTypeConfigs) return null;
      if (!isPhotoTypeCode(containerType)) return null;
      return photoTypeConfig.photoTypeConfigs.find(
        (config) => config.typeCode === containerType && config.isActive
      );
    },
    [photoTypeConfig]
  );

  const getSavedDisplayOrder = useCallback(
    (typeCode: string | undefined): number | undefined => {
      if (!typeCode || !photoTypeRequests?.length) return undefined;
      return photoTypeRequests.find((req) => req.typeCode === typeCode)?.displayOrder;
    },
    [photoTypeRequests]
  );

  const getSnapshotInfo = useCallback(
    (typeCode: string | undefined): { title?: string; subTitle?: string; displayOrder?: number } | null => {
      if (!typeCode || !photoTypeRequests?.length) return null;
      const request = photoTypeRequests.find((req) => req.typeCode === typeCode);
      if (!request) return null;
      return {
        title: request.title,
        subTitle: request.subTitle ?? request.remark,
        displayOrder: request.displayOrder,
      };
    },
    [photoTypeRequests]
  );

  const getCustomTypeInfo = useCallback(
    (typeCode: string): { title: string; subTitle?: string; displayOrder: number } => {
      const snapshotInfo = getSnapshotInfo(typeCode);
      const configItem = photoTypeConfig?.photoTypeConfigs?.find(
        (config) => config.typeCode === typeCode
      );
      const savedRequest = photoTypeRequests?.find((req) => req.typeCode === typeCode);
      return {
        title: snapshotInfo?.title || configItem?.title || typeCode,
        subTitle: snapshotInfo?.subTitle || savedRequest?.remark || undefined,
        displayOrder: snapshotInfo?.displayOrder ?? configItem?.displayOrder ?? DEFAULT_DISPLAY_ORDER,
      };
    },
    [getSnapshotInfo, photoTypeConfig, photoTypeRequests]
  );

  const processExistingCustomMedias = useCallback(
    (customMedias: Record<string, CustomPhotoTypeMediaGroup>): ImageResourceItem[] => {
      return Object.entries(customMedias)
        .map(([typeCode, group]) => {
          const customTypeInfo = getCustomTypeInfo(typeCode);
          const savedDisplayOrder = getSavedDisplayOrder(typeCode);
          const mediaDataList = group.medias.map(createMediaDataFromMedia);
          const imageUrls = group.medias.map((m) => m.containerPhotoSimpleMedia?.mediaUrl);
          return {
            isUsed: group.count > 0,
            imageList: createImageListWithPlaceholders(imageUrls, group.count),
            title: customTypeInfo.title,
            subTitle: customTypeInfo.subTitle,
            displayOrder: savedDisplayOrder ?? customTypeInfo.displayOrder,
            mediaDataList,
          };
        })
        .filter((item) => item.isUsed);
    },
    [getCustomTypeInfo, getSavedDisplayOrder]
  );

  const processRequestedCustomTypes = useCallback(
    (
      customTypeRequests: TaskPhotoTypeRequestResponseDto[],
      customMedias: Record<string, CustomPhotoTypeMediaGroup> | undefined
    ): ImageResourceItem[] => {
      return customTypeRequests
        .map((request) => {
          const group = customMedias?.[request.typeCode];
          const customTypeInfo = getCustomTypeInfo(request.typeCode);
          const mediaDataList = group?.medias?.map(createMediaDataFromMedia) || [];
          const imageUrls = group?.medias?.map((m) => m.containerPhotoSimpleMedia?.mediaUrl) || [];
          return {
            isUsed: request.count > 0,
            imageList: createImageListWithPlaceholders(imageUrls, request.count),
            title: customTypeInfo.title,
            subTitle: request.remark || customTypeInfo.subTitle,
            displayOrder: request.displayOrder,
            mediaDataList,
          };
        })
        .filter((item) => item.isUsed);
    },
    [getCustomTypeInfo]
  );

  const processCustomPhotoTypeMedias = useCallback((): ImageResourceItem[] => {
    const customMedias = currentData?.customPhotoTypeMedias;
    const customTypeRequests = photoTypeRequests?.filter((req) => req.typeCode.startsWith("CUSTOM_")) || [];

    if (customTypeRequests.length === 0) {
      if (!customMedias || Object.keys(customMedias).length === 0) return [];
      return processExistingCustomMedias(customMedias);
    }
    return processRequestedCustomTypes(customTypeRequests, customMedias);
  }, [currentData, photoTypeRequests, processExistingCustomMedias, processRequestedCustomTypes]);

  const processUnifiedPhotoTypeMedias = useCallback(
    (photoTypeMedias: Record<string, PhotoTypeMediaInfo | CustomPhotoTypeItem[]>): ImageResourceItem[] => {
      const result: ImageResourceItem[] = [];

      for (const [key, value] of Object.entries(photoTypeMedias)) {
        if (key === "CUSTOM" && Array.isArray(value)) {
          for (const customItem of value) {
            if (customItem.requiredCount > 0) {
              result.push(createImageResourceItem({
                medias: customItem.medias,
                requiredCount: customItem.requiredCount,
                title: customItem.title ?? `Custom ${customItem.id}`,
                subTitle: customItem.subTitle ?? customItem.remark,
                displayOrder: customItem.displayOrder ?? DEFAULT_DISPLAY_ORDER,
              }));
            }
          }
        } else if (!Array.isArray(value)) {
          const info = value;
          if (info.requiredCount > 0) {
            const snapshotInfo = getSnapshotInfo(info.type);
            const settings = getPhotoTypeSettings(info.type as ContainerMediaType);
            const typeCode = info.type as ContainerMediaType;
            const isExtra = typeCode === "EXTRA";
            result.push(createImageResourceItem({
              medias: info.medias,
              requiredCount: info.requiredCount,
              title: info.title ?? snapshotInfo?.title ?? settings?.title ?? FALLBACK_PHOTO_TYPE_TITLES[typeCode] ?? info.type,
              subTitle: !isExtra ? (info.subTitle ?? snapshotInfo?.subTitle) : undefined,
              remark: isExtra ? (info.subTitle ?? snapshotInfo?.subTitle ?? info.remark) : undefined,
              displayOrder: snapshotInfo?.displayOrder ?? info.displayOrder ?? DEFAULT_DISPLAY_ORDER,
            }));
          }
        }
      }

      return result.sort((a, b) => a.displayOrder - b.displayOrder);
    },
    [getPhotoTypeSettings, getSnapshotInfo]
  );

  const containerImageListMaping = useMemo(() => {
    return Object.entries(containerCountMediaMap).reduce<{
      lastUpdatedAt: undefined | string;
      imageResource: ImageResourceItem[];
    }>(
      (acc, [countKey, mediaKey]) => {
        const count = currentData?.[countKey as keyof ContainerDetailViewDto] ?? 0;
        let lastUpdatedAt = acc.lastUpdatedAt;

        const mediaDataList = count
          ? ((currentData?.[mediaKey as keyof ContainerDetailViewDto] as SimpleContainerMediaDto[])?.map(
              ({ containerPhotoSimpleMedia, containerMediaCreatedAt, coordinate }) => ({
                createAt: containerMediaCreatedAt,
                latitude: coordinate?.latitude ?? undefined,
                longitude: coordinate?.longitude ?? undefined,
                zoomLevel: coordinate?.zoomLevel ? Number(coordinate?.zoomLevel) : undefined,
                mediaCoordinateRange: coordinate?.mediaCoordinateRange || undefined,
                originalFileName: containerPhotoSimpleMedia.originalFileName ?? undefined,
                mediaUrl: containerPhotoSimpleMedia?.mediaUrl ?? undefined,
              })
            ) ?? [])
          : [];

        const imageList = count
          ? ((currentData?.[mediaKey as keyof ContainerDetailViewDto] as SimpleContainerMediaDto[])?.map(
              ({ containerPhotoSimpleMedia, containerMediaUpdatedAt }) => {
                if (!lastUpdatedAt) lastUpdatedAt = containerMediaUpdatedAt;
                if (dayjs(containerMediaUpdatedAt).isAfter(dayjs(lastUpdatedAt))) {
                  lastUpdatedAt = containerMediaUpdatedAt;
                }
                return containerPhotoSimpleMedia.mediaUrl;
              }
            ) ?? [])
          : [];

        const containerType = countKeyConvertToContainerType(countKey as keyof typeof containerCountMediaMap);
        const snapshotInfo = getSnapshotInfo(containerType);
        const photoTypeSetting = isExporter ? getPhotoTypeSettings(containerType) : null;
        const savedDisplayOrder = getSavedDisplayOrder(containerType);

        const resource: ImageResourceItem = {
          isUsed: !!count,
          imageList: [...(imageList as string[]), ...Array.from({ length: count as number }, () => undefined)].slice(
            0,
            count as number
          ),
          title: snapshotInfo?.title ?? photoTypeSetting?.title ?? (containerType ? FALLBACK_PHOTO_TYPE_TITLES[containerType] : undefined),
          subTitle: containerType !== "EXTRA" ? (snapshotInfo?.subTitle ?? undefined) : undefined,
          remark: containerType === "EXTRA" ? (snapshotInfo?.subTitle ?? extraRemark ?? undefined) : undefined,
          displayOrder: snapshotInfo?.displayOrder ?? savedDisplayOrder ?? photoTypeSetting?.displayOrder ?? DEFAULT_DISPLAY_ORDER,
          mediaDataList,
        };

        return {
          lastUpdatedAt,
          imageResource: [...acc.imageResource, resource].filter((item) => item.isUsed),
        };
      },
      { lastUpdatedAt: undefined, imageResource: [] }
    );
  }, [currentData, extraRemark, getPhotoTypeSettings, getSavedDisplayOrder, getSnapshotInfo, isExporter]);

  const customPhotoTypeResources = useMemo(() => processCustomPhotoTypeMedias(), [processCustomPhotoTypeMedias]);

  const sortedImageResource = useMemo(() => {
    if (currentData?.photoTypeMedias && Object.keys(currentData.photoTypeMedias).length > 0) {
      return processUnifiedPhotoTypeMedias(currentData.photoTypeMedias);
    }
    return [...containerImageListMaping.imageResource, ...customPhotoTypeResources].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );
  }, [currentData?.photoTypeMedias, processUnifiedPhotoTypeMedias, containerImageListMaping.imageResource, customPhotoTypeResources]);

  const photoTypeList = currentData ? formatSingleContainerMedia(currentData) : { folderName: "", images: [] };

  const allImageList = useMemo(
    () =>
      sortedImageResource.reduce<string[]>((acc, val) => {
        const validImage = val.imageList.filter((image) => image) as string[];
        return [...acc, ...validImage];
      }, []),
    [sortedImageResource]
  );

  const allMediaDataList = useMemo(
    () =>
      sortedImageResource.reduce<ContainerMediaData[]>(
        (acc, val) => [...acc, ...val.mediaDataList].filter(({ mediaUrl }) => mediaUrl !== undefined),
        [{ latitude: undefined, longitude: undefined, mediaUrl: undefined, originalFileName: undefined, mediaCoordinateRange: undefined, zoomLevel: undefined }]
      ),
    [sortedImageResource]
  );

  const getLastEditedConnectionType = () => {
    if (!currentData?.lastEditedConnectionType) return "[-]";
    if (currentData?.lastEditedConnectionType === "WEB") return "[PC]";
    return `[${currentData?.lastEditedConnectionType}]`;
  };

  const handlePreviewDownloadClick = async () => {
    setIsPreviewDownloadLoading(true);
    try {
      if (!selectImageUrl?.mediaUrl) return;

      if (isExporter) {
        await downloadFile(selectImageUrl.mediaUrl, selectImageUrl.originalFileName ?? "");
      } else {
        await downloadImageWithExif({
          url: selectImageUrl.mediaUrl,
          latitude: Number(selectImageUrl?.latitude),
          longitude: Number(selectImageUrl?.longitude),
          fileName: selectImageUrl.originalFileName,
        });
      }
    } finally {
      setIsPreviewDownloadLoading(false);
    }
  };

  const renderDialogs = () => {
    if (selectDialog === "VIEW_PHOTO") {
      return (
        <ViewPhotoCollectionDialog
          items={allImageList.map((item) => ({ original: item, thumbnail: item }))}
          open={selectDialog === "VIEW_PHOTO"}
          onClose={() => {
            setSelectDialog(null);
            setSelectLoading(null);
          }}
          selectLoading={selectLoading}
          startIndex={startIndex}
          mediaDataList={photoTypeList.images}
          onSelectImageChange={(media) => setSelectImage(media)}
          {...(!isExporter && {
            imgProps: { onContextMenu: (e) => e.preventDefault() },
            thumbnailProps: { onContextMenu: (e) => e.preventDefault() },
            isDownloadBlock: true,
            isLocationInfo: false,
          })}
          photoDetailRightAccessory={
            <Button
              buttonGrade="tertiary"
              buttonColor="black"
              buttonSize={32}
              onClick={handlePreviewDownloadClick}
              isLoading={isPreviewDownloadLoading}
            >
              <Flex alignItems="center" gap={2}>
                <DownloadIcon />
                {t("common:download")}
              </Flex>
            </Button>
          }
        />
      );
    }

    if (isExporter && selectDialog === "EDIT_CONTAINER" && selectEditTab) {
      return (
        <EditContainerInformationDialog
          open
          tabType={selectEditTab}
          containerId={containerId}
          onOpenChange={() => {
            setSelectDialog(null);
            setSelectEditTab(null);
          }}
          refetch={handleContainerRefetch}
          disableTabChange={isUnableEdit}
          mediaDataList={allMediaDataList}
        />
      );
    }
  };

  const renderWritedInAppStatusBadge = () => {
    if (!isExporter) return null;
    if (currentData?.containerWeighingStatus === "WEIGHING_CHECK_COMPLETED") return;
    if (currentData?.isLastWeighingCompletedByApp) {
      if (currentData?.isLastNetWeightUpdatedByWeb) return;
      return renderIsWritedInAppStatus({ type: true, isTooltip: true, t });
    }
    if (!currentData?.isLastNetWeightUpdatedByWeb) return;
  };

  const renderExporterStatusBadges = () => {
    if (!isExporter) return null;
    return (
      <IconContainer>
        {renderContainerWeighCheckedStatus({ type: isContainerCheckCompleted, isTooltip: true, t })}
        {renderWritedInAppStatusBadge()}
      </IconContainer>
    );
  };

  const renderContainerStatusBadge = () => {
    if (!isExporter || !containerStatus) return null;
    return (
      <TextContainer>
        {renderLoadingContainerStatusBadge({
          type: isRefetched ? loadingDetailContainerData.currentData?.containerStatus : containerStatus,
          size: "S",
          t,
        })}
      </TextContainer>
    );
  };

  return (
    <AccordionItem
      value={id}
      onMouseEnter={handlePrefetch}
      onKeyDown={handlePrefetch}
    >
      <StyledAccordionTrigger>
        <AccordionTriggerContainer onClick={(e) => e.stopPropagation()}>
          <Checkbox
            onChange={(e) => {
              e.stopPropagation();
              onCheckChange?.(containerId, e.target.checked);
            }}
            checked={containerCheckList.includes(containerId)}
          />

          <AccordionTriggerTextContainer>
            {renderExporterStatusBadges()}

            <TextAndBadgeContainer>
              <TextContainer>
                <Typo typoType="b7m">{t("common:dynamicContainerNo", { index: index + 1 })}</Typo>
                {!loadingDetailContainerData?.currentData?.containerNo && !containerNo ? (
                  <Typo typoType="b7m" color="blue2">-</Typo>
                ) : (
                  <TableBlueText typoType="b7m" color="systemBlue2" onClick={handleContainerNoClick}>
                    {isRefetched ? loadingDetailContainerData?.currentData?.containerNo : containerNo}
                  </TableBlueText>
                )}
              </TextContainer>

              <TextContainer>
                <Typo typoType="b7m">{t("common:sealNo")}</Typo>
                <Typo typoType="b7m" color="blue2">
                  {isRefetched ? (loadingDetailContainerData?.currentData?.sealNo ?? "-") : (sealNo ?? "-")}
                </Typo>
              </TextContainer>

              {renderContainerStatusBadge()}
            </TextAndBadgeContainer>
          </AccordionTriggerTextContainer>
        </AccordionTriggerContainer>

        <ArrowIcon iconSrc={ArrowDownSvg} iconSize={24} />
      </StyledAccordionTrigger>

      <StyledAccordionContent>
        <AccordionSectionCardContent>
          {isExporter && (
            <UpdateDate>
              <Dot />
              <Typo typoType="b9r" color="gray6">
                {`${t("task:detail.cards.lastUploadDate")} ${getLastEditedConnectionType()}`}
              </Typo>
              <Typo typoType="b9r" color="gray7">
                {getDateDifference({ updateAt: currentData?.updatedAt || "", gmtOffset })}
              </Typo>
            </UpdateDate>
          )}

          <StyledSectionCard
            cardTitle={
              isExporter ? (
                <SectionCardTitle>
                  <Typo typoType="h6">{t("task:detail.cards.containerLoadingInformation")}</Typo>
                  {renderLoadingContainerStatusBadge({
                    type: isRefetched ? loadingDetailContainerData.currentData?.containerStatus : containerStatus,
                    size: "S",
                    t,
                  })}
                </SectionCardTitle>
              ) : (
                t("contract:importer.detail.label.containerLoadingInformation")
              )
            }
            rightAccessory={
              <SectionCardRight>
                <IconButton buttonColor="black" buttonGrade="tertiary" buttonSize={32} onClick={handleContainerRefetch}>
                  <Icon iconSrc={ResetSvg} iconSize={20} />
                </IconButton>

                {isExporter && (
                  <EditButton
                    buttonGrade="tertiary"
                    buttonColor="black"
                    buttonSize={32}
                    disabled={isUnableEdit}
                    onClick={() => {
                      setSelectEditTab("PHOTO");
                      setSelectDialog("EDIT_CONTAINER");
                    }}
                  >
                    <EditIcon color={isUnableEdit ? "gray8" : "gray2"} />
                    {t("task:detail.button.editButton")}
                  </EditButton>
                )}
              </SectionCardRight>
            }
          >
            {!isExporter && (
              <FlexDiv flexDirection="column" gap={8} className="pdbt-32">
                <Typo typoType="b9m" color="gray7">
                  {t("contract:importer.detail.label.weightInformation")}
                </Typo>
                <FlexDiv gap={16}>
                  <FlexDiv className="flex-1" gap={4}>
                    <Typo typoType="b7m">{t("common:grossWeight")}</Typo>
                    <Typo typoType="b7r" color="gray5">
                      {currentData?.grossWeight ? (
                        <>
                          {Number(currentData.grossWeight).toLocaleString("ko-KR")} {currentData.weightUnit}
                        </>
                      ) : (
                        "-"
                      )}
                    </Typo>
                  </FlexDiv>
                  <div className="divider" />
                  <FlexDiv className="flex-1" gap={4}>
                    <Typo typoType="b7m">{t("common:netWeight")}</Typo>
                    <Typo typoType="b7r" color="gray5">
                      {currentData?.netWeight ? (
                        <>
                          {Number(currentData.netWeight).toLocaleString("ko-KR")} {currentData.weightUnit} (
                          {currentData?.convertedNetWeight})
                        </>
                      ) : (
                        "-"
                      )}
                    </Typo>
                  </FlexDiv>
                  <div className="divider" />
                  <FlexDiv className="flex-1" gap={4}>
                    <Typo typoType="b7m">{t("common:unitQuantity")}</Typo>
                    <Typo typoType="b7r" color="gray5">
                      {currentData?.unitQuantity?.toLocaleString("ko-KR")} {currentData?.unitQuantityUnit}
                    </Typo>
                  </FlexDiv>
                </FlexDiv>
              </FlexDiv>
            )}

            <TaskInformationContainer ref={loadingImageRef}>
              {isFetching ? (
                <LoadingContainer height={loadingImageRef?.current?.getBoundingClientRect().height}>
                  <PulseLoader color={colorSet.blue4} />
                </LoadingContainer>
              ) : (
                sortedImageResource.map((item, idx) => (
                  <TaskThumbnail
                    key={idx}
                    title={item.title}
                    subTitle={item.subTitle}
                    remark={item.remark}
                    imageUrlList={item.imageList}
                    onClickImage={(imageUrl) => {
                      if (!currentData) return;
                      setSelectLoading(currentData);
                      setSelectDialog("VIEW_PHOTO");
                      setStartIndex(allImageList.findIndex((img) => img === imageUrl));
                    }}
                  />
                ))
              )}
            </TaskInformationContainer>
          </StyledSectionCard>

          {isExporter && (
            <>
              <StyledSectionCard
                cardTitle={
                  <SectionCardTitle>
                    <Typo typoType="h6">{t("task:detail.cards.weightInformation")}</Typo>
                    {renderContainerWeighCheckedStatus({ type: isContainerCheckCompleted, isTooltip: true, t })}
                    {renderWritedInAppStatusBadge()}
                  </SectionCardTitle>
                }
                rightAccessory={
                  <SectionCardRight>
                    <IconButton buttonColor="black" buttonGrade="tertiary" buttonSize={32} onClick={handleContainerRefetch}>
                      <Icon iconSrc={ResetSvg} iconSize={20} />
                    </IconButton>
                    <EditButton
                      buttonGrade="tertiary"
                      buttonColor="black"
                      buttonSize={32}
                      onClick={() => {
                        setSelectEditTab("WEIGHT");
                        setSelectDialog("EDIT_CONTAINER");
                      }}
                    >
                      <EditIcon color="gray2" />
                      {t("task:detail.button.editButton")}
                    </EditButton>
                  </SectionCardRight>
                }
              >
                {isFetching ? (
                  <LoadingContainer height={loadingWeightRef?.current?.getBoundingClientRect().height}>
                    <PulseLoader color={colorSet.blue4} />
                  </LoadingContainer>
                ) : (
                  <WeightInformationContainer ref={loadingWeightRef}>
                    <WeightInformationContent>
                      <Typo typoType="b9m" color="gray7">
                        {t("task:detail.cards.weightInformationSubTitle")}
                      </Typo>
                      <WeightInformationRows>
                        <SectionCardRow
                          label={t("common:unitQuantity")}
                          value={
                            !currentData?.unitQuantity && !currentData?.unitQuantityUnit ? (
                              "-"
                            ) : (
                              <>
                                {currentData?.unitQuantity?.toLocaleString("ko-KR")}
                                {currentData?.unitQuantityUnit ?? ""}
                              </>
                            )
                          }
                        />
                        <SectionCardRow
                          label={t("common:grossWeight")}
                          value={
                            currentData?.grossWeight && currentData?.weightUnit
                              ? `${Number(currentData?.grossWeight).toLocaleString("ko-KR")} ${currentData?.weightUnit}`
                              : "-"
                          }
                        />
                        <SectionCardRow
                          label={t("common:netWeight")}
                          value={
                            currentData?.netWeight && currentData?.weightUnit
                              ? `${Number(currentData?.netWeight).toLocaleString("ko-KR")} ${currentData?.weightUnit} (${currentData?.convertedNetWeight})`
                              : "-"
                          }
                        />
                      </WeightInformationRows>
                    </WeightInformationContent>

                    <GrayLine />

                    <WeightInformationContent>
                      <Typo typoType="b9m" color="gray7">
                        {t("task:detail.cards.detailInformation")}
                      </Typo>
                      <WeightInformationRows>
                        <SectionCardRow label={t("task:detail.cards.photoSubmittedDate")} value={photoSubmittedDate} />
                        <SectionCardRow
                          label={t("task:detail.cards.firstWeight")}
                          value={
                            currentData?.firstWeight && currentData?.weightUnit
                              ? `${Number(currentData?.firstWeight).toLocaleString("ko-KR")} ${currentData?.weightUnit}`
                              : "-"
                          }
                        />
                        <SectionCardRow
                          label={t("task:detail.cards.secondWeight")}
                          value={
                            currentData?.secondWeight && currentData?.weightUnit
                              ? `${Number(currentData?.secondWeight).toLocaleString("ko-KR")} ${currentData?.weightUnit}`
                              : "-"
                          }
                        />
                        <SectionCardRow
                          label={t("task:detail.cards.weighingTicket")}
                          value={
                            <FileInfoContainer>
                              {currentData?.weighingTicketMedias?.length >= 1
                                ? currentData.weighingTicketMedias.map((weightMedia) => (
                                    <div key={weightMedia.id}>
                                      <figure>
                                        <Typo typoType="b7r" color="gray5">
                                          {weightMedia.originalFileName}
                                        </Typo>
                                      </figure>
                                      <ButtonContainer>
                                        <IconButton buttonSize={24} buttonColor="blue" buttonGrade="secondary">
                                          <Icon
                                            iconSrc={DownloadIndigoSvg}
                                            iconSize={16}
                                            onClick={() => downloadFile(weightMedia.mediaUrl ?? "", weightMedia.originalFileName ?? "")}
                                          />
                                        </IconButton>
                                        <IconButton buttonSize={24} buttonColor="blue" buttonGrade="secondary">
                                          <a href={weightMedia.mediaUrl} target="_blank" rel="noreferrer">
                                            <Icon iconSrc={PreviewSvg} iconSize={16} />
                                          </a>
                                        </IconButton>
                                      </ButtonContainer>
                                    </div>
                                  ))
                                : "-"}
                            </FileInfoContainer>
                          }
                        />
                      </WeightInformationRows>
                    </WeightInformationContent>
                  </WeightInformationContainer>
                )}
              </StyledSectionCard>

              <ConfirmButtonContainer>
                <ConfirmDescription>
                  <ErrorCircleIcon />
                  <Typo typoType="b7m">{t("task:detail.description.confirmWeight")}</Typo>
                </ConfirmDescription>
                <ConfirmButton
                  buttonSize={40}
                  onClick={handleConfirmClick}
                  disabled={isUnableEdit || isContainerCheckCompleted === "WEIGHING_CHECK_COMPLETED"}
                >
                  {isContainerCheckCompleted === "WEIGHING_CHECK_COMPLETED"
                    ? t("task:detail.button.confirmedButton")
                    : t("task:detail.button.confirmButton")}
                </ConfirmButton>
              </ConfirmButtonContainer>
            </>
          )}
        </AccordionSectionCardContent>
      </StyledAccordionContent>

      {renderDialogs()}
    </AccordionItem>
  );
}

export default ContainerAccordionItem;

const ArrowIcon = styled(Icon)`
  transition: 0.3s;
`;

const StyledAccordionTrigger = styled(AccordionTrigger)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  border-bottom: 1px solid ${colorSet.gray10};
  padding: 16px;
  margin: 0;
  cursor: pointer;

  &:hover {
    background: ${colorSet.blue10};
  }

  &[data-state="open"] {
    ${ArrowIcon} {
      transform: rotateZ(-180deg);
    }
  }

  &[data-state="closed"] {
    ${ArrowIcon} {
      transform: rotateZ(0deg);
    }
  }
`;

const AccordionTriggerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AccordionTriggerTextContainer = styled(AccordionTriggerContainer)``;
const IconContainer = styled(AccordionTriggerContainer)``;

const TextAndBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AccordionSectionCardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 16px;
  background: ${colorSet.gray11};
`;

const StyledSectionCard = styled(SectionCard)`
  background: ${colorSet.white};
`;

const SectionCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionCardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UpdateDate = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const EditButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WeightInformationContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 24px;
`;

const WeightInformationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WeightInformationRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GrayLine = styled.div`
  width: 1px;
  height: 100%;
  border-left: 1px solid ${colorSet.gray9};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 4px;
  height: 24px;
`;

const FileInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > div {
    display: flex;
    gap: 8px;
  }

  a {
    line-height: 0px;
  }
`;

const TaskInformationContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(auto, 460px));
  justify-content: center;
  gap: 16px;
`;

const LoadingContainer = styled.div<{ height?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ height }) => height}px;
`;

const EditIcon = styled(EditSvg)<{ color: ColorType }>`
  width: 16px;
  height: 16px;

  path {
    fill: ${({ color }) => colorSet[color]};
  }
`;

const ConfirmButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ConfirmDescription = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConfirmButton = styled(Button)`
  width: 160px;
  text-align: center;
`;

const ErrorCircleIcon = styled(ErrorCircleSvg)`
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  #Shape {
    fill: ${colorSet.blue4};
  }
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  background: ${colorSet.gray7};
  border-radius: 100%;
`;

const DownloadIcon = styled(DownloadSvg)<{ color?: ColorType; disabled?: boolean }>`
  width: 16px;
  height: 16px;

  path {
    ${({ color }) =>
      color &&
      css`
        fill: ${colorSet[color]};
      `}

    ${({ disabled }) =>
      disabled &&
      css`
        fill: ${colorSet.gray8};
      `}
  }
`;

const StyledAccordionContent = styled(AccordionContent)`
  overflow: hidden;
  transform-origin: top;

  &[data-state="open"] {
    animation: ${slideDown} 0.6s ease-in-out;
  }

  &[data-state="closed"] {
    animation: ${slideUp} 0.6s ease-in-out;
  }
`;

const FlexDiv = styled.div<{ flexDirection?: CSSProperties["flexDirection"]; gap?: CSSProperties["gap"] }>`
  display: flex;
  ${({ gap }) =>
    gap &&
    css`
      gap: ${gap}px;
    `};

  ${({ flexDirection }) =>
    flexDirection &&
    css`
      flex-direction: ${flexDirection};
    `};

  &.pdbt-32 {
    padding-bottom: 32px;
  }

  .divider {
    border-right: 1px solid ${colorSet.gray9};
  }

  .flex-1 {
    flex: 1;
    flex-shrink: 0;
  }
`;
